import { create } from 'zustand';
import { api } from '../utils/api';
import type { SlotBlock } from '../pages/auth/SlotBlockedNotice';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  avatar?: string;
  role: { id: string; name: string; displayName: string; permissions: string[] };
  department?: { id: string; name: string; code?: string } | null;
  /** Ids of departments where this user is the head (Department.headId === user.id).
   * Added by /auth/me per TEAMS-SPEC. May be missing on older backends — defaults to []. */
  headOfDepartmentIds?: string[];
  /** Convenience flag from backend; optional. */
  isSuperAdmin?: boolean;
  /**
   * Phase 4 (2026-06-19) — effective scope set, populated by backend when
   * VEDA_RBAC_SCOPED flag is ON. Absent (undefined) when flag is OFF, which
   * is the default/deploy state. Sidebar falls back to hardcoded sections
   * when this is absent or empty.
   */
  scopes?: string[];
}

export interface TenantConfig {
  slug: string;
  name: string;
  enabledModules: string[] | null; // null = all modules (admin/platform)
  pipelineId: string | null;
  planTier: string | null; // 'FREE' | 'STARTER' | 'GROWTH' | 'ENTERPRISE' | null
  isAdmin: boolean;
  /** Custom name for the AI assistant (ENTERPRISE only). Defaults to "Kiaan". */
  aiAssistantName?: string | null;
  /** Company name + logo for screens (backend core/tenant/brand.ts; lib/brand.ts). */
  brand?: { name: string; logoUrl: string | null } | null;
}

interface AuthState {
  user: User | null;
  tenant: TenantConfig | null;
  isLoading: boolean;
  error: string | null;
  /** Set when the server refused the sign-in because the slot is full (409). */
  slotBlock: SlotBlock | null;
  login: (email: string, password: string, takeover?: boolean) => Promise<void>;
  clearSlotBlock: () => void;
  register: (data: { email: string; password: string; firstName: string; lastName: string }) => Promise<void>;
  logout: () => Promise<void>;
  fetchMe: () => Promise<void>;
  refreshTenant: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  tenant: null,
  isLoading: true,
  error: null,
  slotBlock: null,

  login: async (email, password, takeover = false) => {
    try {
      set({ isLoading: true, error: null, slotBlock: null });
      const res = await api.post(
        '/auth/login',
        takeover ? { email, password, takeover: true } : { email, password },
      );

      // 🔒 Load the SAME profile a reload loads (tour bug 9, 2026-10-07).
      // POST /auth/login returns the raw user row — no `scopes` — while
      // GET /auth/me returns getProfile() with `scopes` when VEDA_RBAC_SCOPED
      // is on. Sidebar picks the scope menu only when `user.scopes` is set, so
      // storing the login row gave a different menu until the first reload.
      // The login cookie is already set, so /auth/me answers for this user.
      // If it fails, keep the login row rather than failing a good sign-in.
      let profile = res.data.data.user;
      try {
        const me = await api.get('/auth/me');
        if (me.data?.data) profile = me.data.data;
      } catch {
        // Non-critical — the login row still signs the person in
      }

      // Fetch tenant config before setting isLoading=false
      let tenantData = null;
      try {
        const tenantRes = await api.get('/tenants/current');
        tenantData = tenantRes.data.data || null;
      } catch {
        // Non-critical
      }

      // Set user + tenant + isLoading in ONE state update to prevent race conditions
      set({ user: profile, tenant: tenantData, isLoading: false });
    } catch (err: any) {
      // 🔒 409 is not a failed password — it is the one-login rule. It carries
      // the device holding the slot, and the login screen needs that to offer
      // the way back in. Rendering it as "Login failed" would strand the person.
      if (err.response?.status === 409 && err.response?.data?.error === 'slot_taken') {
        set({ slotBlock: err.response.data.data as SlotBlock, error: null, isLoading: false });
        throw err;
      }
      set({ error: err.response?.data?.error || 'Login failed', isLoading: false });
      throw err;
    }
  },

  clearSlotBlock: () => set({ slotBlock: null }),

  register: async (data) => {
    try {
      set({ isLoading: true, error: null });
      const res = await api.post('/auth/register', data);
      set({ user: res.data.data.user, isLoading: false });
    } catch (err: any) {
      set({ error: err.response?.data?.error || 'Registration failed', isLoading: false });
      throw err;
    }
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      set({ user: null, tenant: null, isLoading: false });
    }
  },

  fetchMe: async () => {
    try {
      set({ isLoading: true });
      const res = await api.get('/auth/me');

      // Fetch tenant in same call to avoid intermediate re-renders
      let tenantData = null;
      try {
        const tenantRes = await api.get('/tenants/current');
        tenantData = tenantRes.data.data || null;
      } catch {
        // Non-critical
      }

      // Set everything in ONE state update
      set({ user: res.data.data, tenant: tenantData, isLoading: false });
    } catch {
      set({ user: null, tenant: null, isLoading: false });
    }
  },

  refreshTenant: async () => {
    try {
      const tenantRes = await api.get('/tenants/current');
      set({ tenant: tenantRes.data.data || null });
    } catch {
      // Non-critical
    }
  },

  clearError: () => set({ error: null }),
}));

// Auto-fetch user on app load (only once)
let _initialized = false;
if (!_initialized) {
  _initialized = true;
  useAuthStore.getState().fetchMe();
}
